import { useState } from 'react'
import {
  Sparkles, Search, CheckCircle2, ShieldAlert, Eye, Layers, Link2, Zap, User,
  Clock, AlertTriangle, CircleSlash, History, ChevronLeft, ChevronRight, HandCoins,
  UserCheck, UserCog, HelpCircle,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import {
  useSGPLabAllowance, useSGPLabFixtureSearch, useSGPLabFamilies, useGenerateSGPLab,
  useGenerateSGPLabOptions, useSGPLabHistory, getSGPLabErrorMessage, getSGPLabErrorCode,
} from '@/hooks/useSGPLab'
import type { SGPLabFixtureSummary, SGPLabFamily, SGPLabResult } from '@/hooks/useSGPLab'
import { TeamCrest } from '@/components/TeamCrest'
import { UpgradePrompt } from '@/components/UpgradePrompt'
import { EmptyState } from '@/components/EmptyState'
import { ResponsibleNotice } from '@/components/ResponsibleNotice'
import { RegisterBetModal } from '@/components/RegisterBetModal'
import { PlayedBadge } from '@/components/PlayedBadge'
import { RiskBadge } from '@/components/RiskBadge'
import { formatOdds, formatProb, cn, formatKickoff } from '@/lib/utils'

/** SGP V2 BLACK -- Personalized SGP Lab (Sprint "SGP V2 BLACK: Personalized SGP Lab",
 * 05/09/2026). Mismo patrón A/B/C ya validado en `PickDetailSheet.tsx`/`ParlayLabPage.tsx`
 * (Product Experience V2) -- cada sección SIEMPRE visible, honesta ante datos vacíos, nunca
 * inventa una razón. */
function ReasonList({ reasons, emptyText }: { reasons: string[]; emptyText: string }) {
  if (!reasons || reasons.length === 0) {
    return <p className="text-xs text-text-dim italic leading-relaxed">{emptyText}</p>
  }
  return (
    <ul className="space-y-1.5">
      {reasons.map((r, i) => (
        <li key={i} className="text-xs text-text-muted leading-relaxed flex items-start gap-2">
          <span className="text-brand mt-1 shrink-0">•</span>
          <span>{r}</span>
        </li>
      ))}
    </ul>
  )
}

// P1.2 (01/10/2026, Timezone Global -- Requisito B): delega al formatter central
// (`resolveDisplayTimeZone()`) en vez de `toLocaleString()` sin zona explícita.
function kickoffLabel(iso?: string | null): string {
  return formatKickoff(iso, { withMonth: false })
}

// Sección 4 del encargo: "rangos iniciales -- PREFERENCIA, nunca obligación."
const TARGET_PRESETS: { label: string; hint: string; min: number; max: number | null }[] = [
  { label: 'Moderado', hint: '@2 - @10', min: 2, max: 10 },
  { label: 'Alto', hint: '@10 - @50', min: 10, max: 50 },
  { label: 'Extremo', hint: '@50+', min: 50, max: null },
]

const FAMILY_LABELS: Record<string, string> = {
  resultado: 'Resultado', goles: 'Goles', corners: 'Córners', tarjetas: 'Tarjetas', tiros: 'Tiros', jugadores: 'Jugadores',
}

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  valid: { label: 'Válido', className: 'text-emerald-400 border-emerald-500/40 bg-emerald-950/30' },
  no_sgp: { label: 'Sin SGP', className: 'text-text-dim border-border bg-panel-elevated' },
  target_not_achievable: { label: 'Objetivo no alcanzado', className: 'text-warn border-warn/40 bg-warn-soft/20' },
  informacion_insuficiente: { label: 'Información insuficiente', className: 'text-text-dim border-border bg-panel-elevated' },
}

function dateLabel(iso?: string | null): string {
  return formatKickoff(iso, { withWeekday: false })
}

// Sección 5 del encargo: AVAILABLE/PENDING/NOT_AVAILABLE -- nunca inventa una alineación,
// siempre honesto sobre qué se sabe al momento de esta generación.
const LINEUP_STATUS_META: Record<string, { label: string; icon: typeof UserCheck; className: string }> = {
  available: { label: 'Alineación confirmada', icon: UserCheck, className: 'text-emerald-400 border-emerald-500/40 bg-emerald-950/30' },
  pending: { label: 'Alineación todavía no confirmada', icon: UserCog, className: 'text-warn border-warn/40 bg-warn-soft/20' },
  not_available: { label: 'Sin alineación esperada para este partido', icon: HelpCircle, className: 'text-text-dim border-border bg-panel-elevated' },
}

function LineupStatusBadge({ status }: { status?: string | null }) {
  if (!status) return null
  const meta = LINEUP_STATUS_META[status]
  if (!meta) return null
  const Icon = meta.icon
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-[9px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full border', meta.className)}>
      <Icon className="h-3 w-3" /> {meta.label}
    </span>
  )
}

function FixturePickerRow({
  fixture, selected, onSelect,
}: { fixture: SGPLabFixtureSummary; selected: boolean; onSelect: () => void }) {
  return (
    <button
      onClick={onSelect}
      className={cn(
        'data-row w-full text-left -mx-1 px-1 rounded-lg transition-colors',
        selected ? 'bg-brand-soft/20' : 'hover:bg-panel-elevated/30'
      )}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex -space-x-2 shrink-0">
          <TeamCrest src={fixture.home_logo} alt={fixture.home_team} size={22} />
          <TeamCrest src={fixture.away_logo} alt={fixture.away_team} size={22} />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-semibold text-text truncate">{fixture.home_team} vs {fixture.away_team}</p>
          <p className="text-[10px] text-text-dim truncate">{fixture.competition_name} · {kickoffLabel(fixture.kickoff_utc)}</p>
        </div>
      </div>
      <span className={cn('shrink-0 h-5 w-5 rounded-full border flex items-center justify-center', selected ? 'bg-brand border-brand' : 'border-border')}>
        {selected && <CheckCircle2 className="h-4 w-4 text-black" />}
      </span>
    </button>
  )
}

// Referencia visual aprobada (mismo lenguaje que Parlay Lab -- dark/neon-green premium):
// hero + selector de los 2 modos reales que ya existían en el encargo original (Sección 2).
function HomeScreen({ onSelect }: { onSelect: (mode: 'mode_a' | 'mode_b') => void }) {
  return (
    <div className="space-y-6">
      <section className="relative overflow-hidden rounded-3xl border border-brand/25 bg-gradient-to-b from-brand-soft/15 via-panel to-panel px-6 py-8 md:px-8 md:py-10">
        <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-brand/10 blur-3xl pointer-events-none" />
        <div className="relative space-y-2.5">
          <span className="eyebrow text-brand inline-flex items-center gap-1.5">
            <Layers className="h-3.5 w-3.5" /> SGP Lab
          </span>
          <h1 className="font-display text-xl md:text-3xl font-bold text-text leading-tight">
            Crea y analiza Same Game Parlays con IA
          </h1>
          <p className="text-xs md:text-sm text-text-muted leading-relaxed max-w-md">
            Elige un partido de Premier League, LaLiga, Serie A, Bundesliga, Ligue 1, Champions
            League o Europa League -- John Anthony combina mercados del MISMO partido con
            correlación real, nunca como si fueran independientes.
          </p>
        </div>
      </section>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          onClick={() => onSelect('mode_a')}
          className="card p-5 rounded-2xl text-left space-y-3 border-brand/40 bg-brand-soft/5 hover:border-brand/60 hover:bg-brand-soft/10 transition group"
        >
          <div className="w-11 h-11 rounded-xl bg-brand/10 border border-brand/30 flex items-center justify-center">
            <Zap className="h-5 w-5 text-brand" />
          </div>
          <div>
            <p className="text-sm font-black text-text">Créalo con John Anthony</p>
            <p className="text-[10px] text-text-dim mt-0.5">La IA construye el SGP por ti</p>
          </div>
        </button>
        <button
          onClick={() => onSelect('mode_b')}
          className="card p-5 rounded-2xl text-left space-y-3 hover:border-cyan/50 hover:bg-cyan/5 transition group"
        >
          <div className="w-11 h-11 rounded-xl bg-panel-elevated border border-border flex items-center justify-center">
            <User className="h-5 w-5 text-cyan" />
          </div>
          <div>
            <p className="text-sm font-black text-text">Enséñame opciones</p>
            <p className="text-[10px] text-text-dim mt-0.5">JA propone varias combinaciones reales, tú eliges</p>
          </div>
        </button>
      </div>
    </div>
  )
}

function ConfigureStep({
  onGenerate, generating, submitLabel = 'Construir con John Anthony',
}: {
  onGenerate: (input: { fixture: SGPLabFixtureSummary; families: SGPLabFamily[]; targetMin: number | null; targetMax: number | null }) => void
  generating: boolean
  submitLabel?: string
}) {
  const [query, setQuery] = useState('')
  const [selectedFixture, setSelectedFixture] = useState<SGPLabFixtureSummary | null>(null)
  const [selectedFamilies, setSelectedFamilies] = useState<SGPLabFamily[]>([])
  const [targetMin, setTargetMin] = useState('')
  const [targetMax, setTargetMax] = useState('')

  const { data: fixturesData, isLoading: fixturesLoading } = useSGPLabFixtureSearch(true, query)
  const { data: familiesData } = useSGPLabFamilies(true, selectedFixture?.fixture_id ?? null)

  const selectFixture = (fixture: SGPLabFixtureSummary) => {
    if (selectedFixture?.fixture_id === fixture.fixture_id) {
      setSelectedFixture(null)
    } else {
      setSelectedFixture(fixture)
      setSelectedFamilies([])
    }
  }
  const toggleFamily = (family: SGPLabFamily) => {
    setSelectedFamilies((prev) => (prev.includes(family) ? prev.filter((f) => f !== family) : [...prev, family]))
  }
  const applyPreset = (preset: (typeof TARGET_PRESETS)[number]) => {
    setTargetMin(String(preset.min))
    setTargetMax(preset.max != null ? String(preset.max) : '')
  }

  const canGenerate = selectedFixture != null && selectedFamilies.length >= 1 && !generating

  return (
    <div className="space-y-6">
      {/* 1. Elegir partido -- Sección 1 del encargo: solo Big 5 + UCL, ya filtrado por el backend */}
      <section className="space-y-2">
        <p className="eyebrow">1 · Elige un partido (Premier League, LaLiga, Serie A, Bundesliga, Ligue 1, Champions)</p>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-dim" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Busca un equipo…"
            className="w-full bg-panel-elevated border border-border rounded-xl pl-9 pr-3 py-2.5 text-sm text-text placeholder:text-text-dim outline-none focus:border-brand/50"
          />
        </div>
        <div className="card p-2 max-h-64 overflow-y-auto divide-y divide-border/40">
          {fixturesLoading && <p className="text-xs text-text-dim p-3">Buscando partidos…</p>}
          {!fixturesLoading && (fixturesData?.fixtures?.length ?? 0) === 0 && (
            <p className="text-xs text-text-dim p-3">Sin partidos disponibles con cobertura real ahora mismo en estas competiciones.</p>
          )}
          {fixturesData?.fixtures?.map((f) => (
            <FixturePickerRow
              key={f.fixture_id} fixture={f}
              selected={selectedFixture?.fixture_id === f.fixture_id}
              onSelect={() => selectFixture(f)}
            />
          ))}
        </div>
      </section>

      {/* 2. Familias/mercados de ESE partido */}
      <section className="space-y-2">
        <p className="eyebrow">2 · Familias que te interesan de este partido</p>
        {selectedFixture == null ? (
          <p className="text-xs text-text-dim">Elige un partido primero para ver qué familias tienen cobertura real.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {(familiesData?.families ?? []).map((f) => (
              <button
                key={f.family}
                disabled={!f.available}
                onClick={() => toggleFamily(f.family)}
                className={cn(
                  'text-xs font-bold rounded-xl px-3 py-2.5 border transition text-left',
                  !f.available && 'opacity-35 cursor-not-allowed border-border/40 text-text-dim',
                  f.available && selectedFamilies.includes(f.family) && 'bg-brand text-black border-brand',
                  f.available && !selectedFamilies.includes(f.family) && 'bg-panel-elevated text-text border-border hover:border-brand/40'
                )}
              >
                {f.label}
                {!f.available && <span className="block text-[9px] font-normal mt-0.5">Sin cobertura real</span>}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* 3. Rango de cuota objetivo (opcional) -- Sección 4 del encargo */}
      <section className="space-y-2">
        <p className="eyebrow">3 · Rango de cuota objetivo (opcional)</p>
        <div className="grid grid-cols-3 gap-2">
          {TARGET_PRESETS.map((preset) => (
            <button
              key={preset.label}
              onClick={() => applyPreset(preset)}
              className={cn(
                'text-center rounded-xl px-2 py-2.5 border transition',
                targetMin === String(preset.min) ? 'bg-brand-soft/20 border-brand/50' : 'bg-panel-elevated border-border hover:border-brand/30'
              )}
            >
              <p className="text-xs font-black text-text">{preset.label}</p>
              <p className="text-[10px] text-text-dim figure">{preset.hint}</p>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <input
            type="number" step="0.1" min="1" placeholder="Mín."
            value={targetMin} onChange={(e) => setTargetMin(e.target.value)}
            className="w-24 bg-panel-elevated border border-border rounded-xl px-3 py-2 text-sm text-text outline-none focus:border-brand/50 figure"
          />
          <span className="text-text-dim text-xs">a</span>
          <input
            type="number" step="0.1" min="1" placeholder="Máx."
            value={targetMax} onChange={(e) => setTargetMax(e.target.value)}
            className="w-24 bg-panel-elevated border border-border rounded-xl px-3 py-2 text-sm text-text outline-none focus:border-brand/50 figure"
          />
        </div>
        <p className="text-[10px] text-text-dim leading-relaxed">
          Si no cabe dentro del rango sin sacrificar calidad, John Anthony te avisa y muestra la mejor
          construcción real disponible -- nunca fuerza piernas malas para llegar al objetivo.
        </p>
      </section>

      <button
        disabled={!canGenerate}
        onClick={() =>
          selectedFixture &&
          onGenerate({
            fixture: selectedFixture, families: selectedFamilies,
            targetMin: targetMin ? Number(targetMin) : null, targetMax: targetMax ? Number(targetMax) : null,
          })
        }
        className={cn('btn-brand-app w-full py-3.5 text-sm', !canGenerate && 'opacity-40 cursor-not-allowed')}
      >
        <Sparkles className="h-4 w-4" />
        {generating ? 'Construyendo…' : submitLabel}
      </button>
      {generating && (
        <p className="text-[10px] text-text-dim text-center">
          Puede tardar hasta 1-2 minutos si John Anthony no analizó este partido recientemente.
        </p>
      )}
      {selectedFixture == null && (
        <p className="text-[10px] text-text-dim text-center">Elige un partido para continuar.</p>
      )}
    </div>
  )
}

function LegRow({ leg }: { leg: SGPLabResult['legs'][number] }) {
  const bookmaker = leg.odds_source?.bookmaker
  const capturedText = leg.odds_source?.reference_text
  return (
    <div className="data-row items-start">
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-text truncate">{leg.selection_label ?? leg.selection}</p>
        <p className="text-[10px] text-text-dim truncate">{leg.family_label ?? leg.family}</p>
        {(bookmaker || capturedText) && (
          <p className="text-[9px] text-text-dim/70 truncate mt-0.5">
            {bookmaker ? `Cuota observada en ${bookmaker}` : capturedText}
          </p>
        )}
      </div>
      <div className="flex flex-col items-end shrink-0">
        <span className="figure text-sm font-bold text-text">{formatOdds(leg.odds)}</span>
        {leg.fair_odds != null && (
          <span className="figure text-[9px] text-text-dim">justa {formatOdds(leg.fair_odds)}</span>
        )}
      </div>
    </div>
  )
}

export function ResultStep({
  result, fixture, onReset,
}: { result: SGPLabResult; fixture?: SGPLabFixtureSummary | null; onReset: () => void }) {
  const [showPlayModal, setShowPlayModal] = useState(false)
  const hasPlayableLegs = result.status === 'valid' || result.status === 'target_not_achievable'

  if (result.status === 'informacion_insuficiente') {
    return (
      <div className="space-y-4">
        <EmptyState
          icon={CircleSlash}
          title="Información insuficiente"
          body={result.failure_reason ?? 'No hay datos suficientes para construir un SGP con este partido/familias todavía.'}
        />
        <button onClick={onReset} className="btn-secondary-app w-full py-3">Elegir otro partido</button>
      </div>
    )
  }
  if (result.status === 'no_sgp') {
    return (
      <div className="space-y-4">
        <EmptyState
          icon={CircleSlash}
          title="Sin SGP defendible"
          body={result.failure_reason ?? 'John Anthony no encontró una combinación real con sustento suficiente en este partido.'}
        />
        <button onClick={onReset} className="btn-secondary-app w-full py-3">Ajustar preferencias</button>
      </div>
    )
  }

  // Corrección real de usuario (05/09/2026, Sección 2 -- historial): al abrir desde "Mis
  // SGP" no hay una selección viva de `ConfigureStep` (con escudos) -- se usa `result.
  // fixture`, resuelto por el backend en lectura, como respaldo con el mismo nombre real
  // de partido aunque no tenga escudos.
  const displayFixture = fixture ?? result.fixture

  return (
    <div className="space-y-6">
      {displayFixture && (
        <div className="flex items-center gap-3 min-w-0">
          {fixture && (
            <div className="flex -space-x-2 shrink-0">
              <TeamCrest src={fixture.home_logo} alt={fixture.home_team} size={26} />
              <TeamCrest src={fixture.away_logo} alt={fixture.away_team} size={26} />
            </div>
          )}
          <div className="min-w-0">
            <p className="text-sm font-bold text-text truncate">{displayFixture.home_team} vs {displayFixture.away_team}</p>
            <p className="text-[10px] text-text-dim truncate">{displayFixture.competition_name} · {kickoffLabel(displayFixture.kickoff_utc)}</p>
          </div>
        </div>
      )}

      {result.lineup_status && (
        <div>
          <LineupStatusBadge status={result.lineup_status} />
        </div>
      )}

      {result.status === 'target_not_achievable' && (
        <div className="card border-warn/30 bg-warn-soft/20 flex items-start gap-2.5 p-3.5 rounded-2xl">
          <AlertTriangle className="h-4 w-4 text-warn shrink-0 mt-0.5" />
          <p className="text-xs text-warn leading-relaxed">
            {result.target_not_achievable_reason ?? 'No se alcanzó el rango de cuota pedido -- se muestra la mejor construcción real disponible.'}
          </p>
        </div>
      )}

      <div className="bg-emerald-950/40 border border-emerald-500/40 p-4 rounded-2xl space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">SGP construido por JA</span>
          <span className="text-[10px] font-bold text-text-dim">{result.legs.length} piernas</span>
        </div>
        <div className="flex items-end justify-between">
          <div>
            <p className="text-[9px] font-bold uppercase text-text-dim">Cuota combinada (JA)</p>
            <p className="figure text-3xl font-black text-emerald-400">@{formatOdds(result.combined_odds)}</p>
          </div>
          {result.joint_probability_adjusted != null && (
            <div className="text-right">
              <p className="text-[9px] font-bold uppercase text-text-dim">Probabilidad conjunta</p>
              <p className="figure text-lg font-bold text-cyan">{formatProb(result.joint_probability_adjusted, 1)}</p>
            </div>
          )}
        </div>
        {result.risk_score != null && (
          <div className="flex items-center justify-between pt-1">
            <RiskBadge level={result.risk_score >= 55 ? 'alto' : result.risk_score >= 30 ? 'medio' : 'bajo'} />
            <p className="text-[10px] text-text-dim">Correlación {result.correlation_level ?? 'baja'}</p>
          </div>
        )}
      </div>

      {result.thesis && (
        <section className="space-y-2">
          <p className="text-[11px] font-black uppercase tracking-wider text-text-dim">Tesis de John Anthony</p>
          <div className="bg-panel-elevated/40 border border-border/40 p-4 rounded-2xl flex items-start gap-2.5">
            <Link2 className="h-4 w-4 text-brand shrink-0 mt-0.5" />
            <p className="text-xs text-text-muted leading-relaxed">{result.thesis}</p>
          </div>
        </section>
      )}

      <section className="space-y-2">
        <p className="eyebrow">Piernas</p>
        <div className="card p-3 divide-y divide-border/50">
          {result.legs.map((leg, i) => <LegRow key={i} leg={leg} />)}
        </div>
      </section>

      <section className="space-y-2">
        <p className="text-[11px] font-black uppercase tracking-wider text-text-dim">Por qué le gusta a JA</p>
        <div className="bg-panel-elevated/40 border border-border/40 p-4 rounded-2xl flex items-start gap-2.5">
          <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
          <ReasonList reasons={result.explanation.why} emptyText="Sin factores adicionales destacados para esta combinación." />
        </div>
      </section>
      <section className="space-y-2">
        <p className="text-[11px] font-black uppercase tracking-wider text-text-dim">Qué puede fallar</p>
        <div className="bg-panel-elevated/40 border border-border/40 p-4 rounded-2xl flex items-start gap-2.5">
          <ShieldAlert className="h-4 w-4 text-warn shrink-0 mt-0.5" />
          <ReasonList reasons={result.explanation.risks} emptyText="John Anthony no identifica riesgos materiales adicionales." />
        </div>
      </section>
      <section className="space-y-2">
        <p className="text-[11px] font-black uppercase tracking-wider text-text-dim">Qué hay que vigilar</p>
        <div className="bg-panel-elevated/40 border border-border/40 p-4 rounded-2xl flex items-start gap-2.5">
          <Eye className="h-4 w-4 text-cyan shrink-0 mt-0.5" />
          <ReasonList reasons={result.explanation.watch} emptyText="Sin condiciones adicionales que vigilar." />
        </div>
      </section>

      {result.session && (
        <p className="text-[10px] text-text-dim text-center">
          {result.session.unlimited
            ? 'Intentos ilimitados en esta sesión.'
            : `Intento ${result.session.attempts_used}/${result.session.attempts_limit} en esta sesión (mismo partido).`}
        </p>
      )}

      {/* Sprint "Mi Banca V2" (10/09/2026, Sección 1/2 del encargo): "LA JUGUÉ" -- completa
          el enganche que `SGPLabRun.linked_personal_bet_id` ya reservaba desde el sprint
          original de SGP Lab, MISMO botón/modal reutilizado del resto de productos. */}
      {hasPlayableLegs && (
        result.my_bet ? (
          <PlayedBadge bet={result.my_bet} />
        ) : (
          <button
            onClick={() => setShowPlayModal(true)}
            className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-panel-elevated border border-violet/40 text-violet hover:bg-violet/10 text-[10px] font-black uppercase tracking-wider transition"
          >
            <HandCoins className="h-3.5 w-3.5" /> La jugué
          </button>
        )
      )}
      {showPlayModal && (
        <RegisterBetModal
          onClose={() => setShowPlayModal(false)}
          matchLabel={displayFixture ? `SGP Lab · ${displayFixture.home_team} vs ${displayFixture.away_team}` : 'SGP Lab'}
          selectionLabel={result.legs.map((l) => l.selection_label ?? l.selection).join(' + ')}
          referenceOdds={result.combined_odds ?? 0}
          sgpLabRunId={result.id}
        />
      )}

      <ResponsibleNotice />
      <button onClick={onReset} className="btn-secondary-app w-full py-3">Construir otro SGP</button>
    </div>
  )
}

/** Corrección real de usuario (05/09/2026, Sección 2 del encargo): "las generaciones ya se
 * persisten, pero el usuario no tiene una experiencia visible para consultarlas" -- MIS SGP.
 * Mismo patrón que `ParlayLabPage.tsx::HistoryList` -- nunca mezcla con apuestas jugadas/
 * P&L/Banca todavía. */
function HistoryEntryCard({ run, onOpen }: { run: SGPLabResult; onOpen: () => void }) {
  const status = STATUS_LABELS[run.status] ?? { label: run.status, className: 'text-text-dim border-border bg-panel-elevated' }
  const targetLabel = run.target_odds_min != null || run.target_odds_max != null
    ? `Objetivo @${run.target_odds_min ?? '—'}-@${run.target_odds_max ?? '—'}`
    : 'Sin objetivo de cuota'

  return (
    <button onClick={onOpen} className="card p-3.5 rounded-2xl space-y-2 w-full text-left hover:border-violet/40 transition">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-bold text-text-dim">{dateLabel(run.created_at)}</span>
        <span className={cn('text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border', status.className)}>
          {status.label}
        </span>
      </div>
      {run.fixture && (
        <p className="text-xs font-semibold text-text truncate">{run.fixture.home_team} vs {run.fixture.away_team}</p>
      )}
      <p className="text-[10px] text-text-dim">
        {run.requested_families.map((f) => FAMILY_LABELS[f] ?? f).join(' · ')} · {targetLabel}
      </p>
      <div className="flex items-center justify-between pt-1">
        <span className="text-[10px] text-text-dim">{run.legs.length} pierna{run.legs.length === 1 ? '' : 's'}</span>
        <div className="flex items-center gap-3">
          {run.combined_odds != null && (
            <span className="figure text-sm font-bold text-text">@{formatOdds(run.combined_odds)}</span>
          )}
          {run.joint_probability_adjusted != null && (
            <span className="figure text-xs font-bold text-cyan">{formatProb(run.joint_probability_adjusted, 1)}</span>
          )}
          <ChevronRight className="h-3.5 w-3.5 text-text-dim" />
        </div>
      </div>
    </button>
  )
}

function HistoryList({ onOpenRun, onBack }: { onOpenRun: (run: SGPLabResult) => void; onBack: () => void }) {
  const [offset, setOffset] = useState(0)
  const { data, isLoading } = useSGPLabHistory(true, offset)
  const items = data?.items ?? []

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="flex items-center gap-1 text-xs font-bold text-text-dim">
        <ChevronLeft className="h-4 w-4" /> Volver
      </button>
      <p className="eyebrow">Mis SGP</p>
      {isLoading && <p className="text-xs text-text-dim">Cargando historial…</p>}
      {!isLoading && items.length === 0 && (
        <EmptyState icon={History} title="Sin generaciones todavía" body="Cuando construyas un SGP con John Anthony, aparecerá aquí -- lo hayas jugado o no." />
      )}
      <div className="space-y-2.5">
        {items.map((run) => <HistoryEntryCard key={run.id} run={run} onOpen={() => onOpenRun(run)} />)}
      </div>
      {items.length > 0 && (
        <div className="flex items-center justify-between pt-1">
          <button disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - 20))} className="text-xs font-bold text-text-dim disabled:opacity-30">← Más recientes</button>
          <button disabled={items.length < 20} onClick={() => setOffset((o) => o + 20)} className="text-xs font-bold text-text-dim disabled:opacity-30">Más antiguas →</button>
        </div>
      )}
    </div>
  )
}

/** Modo B -- "Enséñame opciones" (Sección 11 del encargo, 24/09/2026): cada opción YA es un
 * `SGPLabResult` real, persistido de forma independiente -- "Usar este parlay" solo cambia
 * cuál se muestra en `ResultStep`, sin ninguna llamada nueva. Si el motor no encuentra
 * calidad suficiente, `options` trae una sola entrada NO_SGP/INFORMACION_INSUFICIENTE (ver
 * `generate_sgp_options`) -- se muestra igual que cualquier resultado fallido, nunca se
 * fabrican opciones ficticias para llenar pantalla. */
function OptionCard({ option, index, onUse }: { option: SGPLabResult; index: number; onUse: () => void }) {
  if (option.status !== 'valid' && option.status !== 'target_not_achievable') {
    return null
  }
  return (
    <div className="card p-4 rounded-2xl space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-black uppercase tracking-widest text-cyan">Opción {index + 1}</span>
        <span className="figure text-lg font-black text-text">@{formatOdds(option.combined_odds)}</span>
      </div>
      <div className="space-y-1.5">
        {option.legs.slice(0, 4).map((leg, i) => (
          <div key={i} className="flex items-center justify-between gap-2 text-xs">
            <span className="text-text-muted truncate">{leg.selection_label ?? leg.selection}</span>
            <span className="figure text-text-dim shrink-0">{formatOdds(leg.odds)}</span>
          </div>
        ))}
        {option.legs.length > 4 && (
          <p className="text-[10px] text-text-dim">+{option.legs.length - 4} pierna{option.legs.length - 4 === 1 ? '' : 's'} más</p>
        )}
      </div>
      <div className="flex items-center justify-between pt-1">
        {option.joint_probability_adjusted != null && (
          <div>
            <p className="text-[9px] font-bold uppercase text-text-dim">Probabilidad JA</p>
            <p className="figure text-sm font-bold text-cyan">{formatProb(option.joint_probability_adjusted, 1)}</p>
          </div>
        )}
        {option.risk_score != null && (
          <RiskBadge level={option.risk_score >= 55 ? 'alto' : option.risk_score >= 30 ? 'medio' : 'bajo'} />
        )}
      </div>
      <button onClick={onUse} className="btn-brand-app w-full py-2.5 text-xs">Usar este parlay</button>
    </div>
  )
}

export function OptionsStep({
  options, onUse, onBack,
}: { options: SGPLabResult[]; onUse: (option: SGPLabResult) => void; onBack: () => void }) {
  const playable = options.filter((o) => o.status === 'valid' || o.status === 'target_not_achievable')

  if (playable.length === 0) {
    const first = options[0]
    return (
      <div className="space-y-4">
        <EmptyState
          icon={CircleSlash}
          title={first?.status === 'no_sgp' ? 'Sin SGP defendible' : 'Información insuficiente'}
          body={first?.failure_reason ?? 'John Anthony no encontró ninguna combinación real con calidad suficiente para este partido/familias.'}
        />
        <button onClick={onBack} className="btn-secondary-app w-full py-3">Ajustar preferencias</button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <p className="eyebrow">Opciones de John Anthony</p>
      <p className="text-xs text-text-muted leading-relaxed">
        {playable.length} combinación{playable.length === 1 ? '' : 'es'} real{playable.length === 1 ? '' : 'es'}, ya evaluada
        {playable.length === 1 ? '' : 's'} con probabilidad conjunta, calidad y correlación -- elige la que prefieras.
      </p>
      <div className="space-y-3">
        {playable.map((option, i) => (
          <OptionCard key={option.id} option={option} index={i} onUse={() => onUse(option)} />
        ))}
      </div>
      <button onClick={onBack} className="btn-secondary-app w-full py-3">Ajustar preferencias</button>
    </div>
  )
}

type SGPLabMode = 'home' | 'mode_a' | 'mode_b'

export function SGPLabPage() {
  const { user } = useAuth()
  const isBlack = user?.plan === 'BLACK' || user?.plan === 'ADMIN' || user?.role === 'ADMIN'
  const [mode, setMode] = useState<SGPLabMode>('home')
  const [result, setResult] = useState<SGPLabResult | null>(null)
  const [resultFixture, setResultFixture] = useState<SGPLabFixtureSummary | null>(null)
  const [options, setOptions] = useState<SGPLabResult[] | null>(null)
  const [optionsFixture, setOptionsFixture] = useState<SGPLabFixtureSummary | null>(null)
  const [view, setView] = useState<'main' | 'history'>('main')
  const { data: allowance } = useSGPLabAllowance(isBlack)
  const generateMutation = useGenerateSGPLab()
  const optionsMutation = useGenerateSGPLabOptions()

  const resetToHome = () => {
    setMode('home'); setResult(null); setResultFixture(null); setOptions(null); setOptionsFixture(null)
    generateMutation.reset(); optionsMutation.reset()
  }

  if (!isBlack) {
    return (
      <div className="p-4 space-y-4">
        <div className="flex items-center gap-1.5 eyebrow text-exact">
          <Layers className="h-3.5 w-3.5" /> SGP Lab
        </div>
        <UpgradePrompt requiredPlan="BLACK" feature="SGP Lab personalizado" />
      </div>
    )
  }

  if (view === 'history') {
    return (
      <div className="p-4 lg:max-w-2xl lg:mx-auto">
        <HistoryList
          onBack={() => setView('main')}
          onOpenRun={(run) => { setResult(run); setResultFixture(null); setOptions(null); setMode('mode_a'); setView('main') }}
        />
      </div>
    )
  }

  const activeMutation = mode === 'mode_b' ? optionsMutation : generateMutation
  const sessionLimitErrorCode = activeMutation.isError ? getSGPLabErrorCode(activeMutation.error) : undefined
  const sessionLimitReached = sessionLimitErrorCode === 'SESSION_LIMIT_REACHED'

  return (
    <div className="p-4 space-y-5 lg:max-w-2xl lg:mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 eyebrow text-exact">
          <Layers className="h-3.5 w-3.5" /> SGP Lab
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setView('history')} className="flex items-center gap-1 text-[10px] font-bold text-text-dim hover:text-text">
            <History className="h-3.5 w-3.5" /> Mis SGP
          </button>
          {allowance && (
            <span className="text-[10px] font-bold text-text-dim font-mono">
              {allowance.unlimited ? 'Ilimitado' : `${allowance.sessions_remaining}/${allowance.sessions_limit} sesiones hoy`}
            </span>
          )}
        </div>
      </div>

      {mode === 'home' && !result && !options && (
        <HomeScreen onSelect={(m) => setMode(m)} />
      )}

      {mode !== 'home' && !result && !options && (
        <button onClick={resetToHome} className="flex items-center gap-1 text-xs font-bold text-text-dim">
          <ChevronLeft className="h-4 w-4" /> Volver
        </button>
      )}

      {sessionLimitReached ? (
        <div className="space-y-4">
          <EmptyState
            icon={Clock}
            title="Límite de sesión alcanzado"
            body={getSGPLabErrorMessage(activeMutation.error)}
          />
          <button onClick={() => setView('history')} className="btn-secondary-app w-full py-3">Revisar mis SGP</button>
        </div>
      ) : result ? (
        <ResultStep result={result} fixture={resultFixture} onReset={resetToHome} />
      ) : options ? (
        <OptionsStep
          options={options}
          onBack={() => setOptions(null)}
          onUse={(picked) => { setResult(picked); setResultFixture(optionsFixture) }}
        />
      ) : mode === 'mode_a' ? (
        <>
          <p className="text-xs text-text-muted leading-relaxed">
            Elige un partido, las familias de mercado que te interesan y (si quieres) un rango de
            cuota objetivo -- John Anthony construye el Same Game Parlay más defendible.
          </p>
          <ConfigureStep
            generating={generateMutation.isPending}
            submitLabel="Construir con John Anthony"
            onGenerate={({ fixture, families, targetMin, targetMax }) => {
              generateMutation.mutate(
                { fixtureId: fixture.fixture_id, families, targetOddsMin: targetMin, targetOddsMax: targetMax },
                { onSuccess: (data) => { setResult(data); setResultFixture(fixture) } }
              )
            }}
          />
          {generateMutation.isError && !sessionLimitReached && (
            <div className="card border-danger/30 bg-danger-soft/10 p-3.5 rounded-2xl flex items-start gap-2.5">
              <AlertTriangle className="h-4 w-4 text-danger shrink-0 mt-0.5" />
              <p className="text-xs text-danger leading-relaxed">{getSGPLabErrorMessage(generateMutation.error)}</p>
            </div>
          )}
        </>
      ) : mode === 'mode_b' ? (
        <>
          <p className="text-xs text-text-muted leading-relaxed">
            Elige un partido y tus familias de interés -- John Anthony propone varias combinaciones
            reales y viables, ya evaluadas, para que elijas la que prefieras.
          </p>
          <ConfigureStep
            generating={optionsMutation.isPending}
            submitLabel="Ver opciones"
            onGenerate={({ fixture, families, targetMin, targetMax }) => {
              optionsMutation.mutate(
                { fixtureId: fixture.fixture_id, families, targetOddsMin: targetMin, targetOddsMax: targetMax },
                { onSuccess: (data) => { setOptions(data.options); setOptionsFixture(fixture) } }
              )
            }}
          />
          {optionsMutation.isError && !sessionLimitReached && (
            <div className="card border-danger/30 bg-danger-soft/10 p-3.5 rounded-2xl flex items-start gap-2.5">
              <AlertTriangle className="h-4 w-4 text-danger shrink-0 mt-0.5" />
              <p className="text-xs text-danger leading-relaxed">{getSGPLabErrorMessage(optionsMutation.error)}</p>
            </div>
          )}
        </>
      ) : null}
    </div>
  )
}
